import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { CreditTransaction } from "@/lib/types";
import { formatDateTime } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Minus } from "lucide-react";

interface CreditHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreditHistoryModal = ({ open, onOpenChange }: CreditHistoryModalProps) => {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      fetchCreditHistory();
    }
  }, [open]);

  const fetchCreditHistory = async () => {
    setLoading(true);
    try {
      const data = await api.getCreditHistory();
      setTransactions(data);
    } catch (error) {
      console.error('Error fetching credit history:', error);
      toast({
        title: t('creditHistory.couldNotLoad'),
        description: error instanceof Error ? error.message : t('creditHistory.noTransactions'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDateTimeLocal = (dateString: string) => {
    return formatDateTime(dateString, 'sv-SE');
  };

  const formatPrice = (amountCents: number | null, currency: string | null) => {
    if (amountCents === null || amountCents === undefined) return '–';
    const amount = amountCents / 100;
    const currencyUpper = currency?.toUpperCase() || '';
    return `${amount.toFixed(2).replace('.', ',')} ${currencyUpper}`.trim();
  };

  const getCreditBadge = (amount: number) => {
    if (amount > 0) {
      return (
        <Badge variant="secondary" className="gap-1 bg-green-500/10 text-green-600 border-green-500/20">
          <Plus className="h-3 w-3" />
          +{amount}
        </Badge>
      );
    } else {
      return (
        <Badge variant="secondary" className="gap-1 bg-red-500/10 text-red-600 border-red-500/20">
          <Minus className="h-3 w-3" />
          {amount}
        </Badge>
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>{t('creditHistory.title')}</DialogTitle>
          <DialogDescription>
            {t('creditHistory.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('creditHistory.noTransactions')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('creditHistory.date')}</TableHead>
                  <TableHead>{t('creditHistory.descriptionCol')}</TableHead>
                  <TableHead className="text-right">{t('creditHistory.price')}</TableHead>
                  <TableHead className="text-right">{t('creditHistory.credits')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatDateTimeLocal(transaction.created_at)}
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <span className="text-sm">{transaction.description || '–'}</span>
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground whitespace-nowrap">
                      {formatPrice(transaction.amount_cents, transaction.currency)}
                    </TableCell>
                    <TableCell className="text-right">
                      {getCreditBadge(transaction.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};